import { useEffect, useState, useCallback, useRef } from 'react';
import classNames from 'classnames';
import Form, { Item as FormItem, GroupItem } from 'devextreme-react/form';
import dayjs from 'dayjs';
import { formatDate } from 'devextreme/localization';
import FileUploader from 'devextreme-react/file-uploader';
import DateBox from 'devextreme-react/date-box';
import Validator, {RequiredRule, CustomRule} from 'devextreme-react/validator';
import { TextArea } from 'devextreme-react/text-area';
import GetFetch from '../../hooks/GetFetch';
import notify from 'devextreme/ui/notify';
import './CRMABillForm.scss';

// const fileExtensions = ['.csv'];

/** @typedef {import('../../types/crmaBill').CRMABillData} CRMABillData */

/**
 * @param {CRMABillData} data
 * @param {boolean} editing
 * @param {string} fileExtensions
 * @param {function(field, value): void} updateField
 */

export const CRMABillFormDetails = ({ data, editing, fileExtensions, updateField, updateObject }) => {

    const [formDataDefaults, setFormDataDefaults] = useState(data);
    const validFileNames = ['adjustments_bfc.csv',
        'adjustments.csv',
        'dhs_bill_cntct.csv',
        'fpsds_gsa.csv',
        'special_rates.csv',
        'ab_address.csv',
        'dhsbldresponsibilityall.csv',
        'dhsbuilding.csv',
        'dhsoas.csv'];
    const billDateOptions = { maxZoomLevel: 'year', minZoomLevel: 'decade' };
    const billPeriodOptions = { maxZoomLevel: 'decade', minZoomLevel: 'year' };
    const isSubset = (parent, subset) => {
        return subset.every(element => parent.includes(element));
    };

    useEffect(() => {
        setFormDataDefaults(data);
    }, []);


    const onDataChanged = (field, newValue) => {
        setFormDataDefaults({ ...formDataDefaults, ...{ [field]: newValue } });
        updateField(field, newValue);
    };

    const hasValidFiles = (e) => {
        let isValid = true;
        const hasValidCount = e.value.length === 9;
        const hasValidNames = isSubset(validFileNames, e.value.map(f => f.name.toLowerCase()));
        
        if (!hasValidCount) {
            e.rule.message = "9 Files Required."; // Toggles to file count message
            return hasValidCount;
        };
        
        if (!hasValidNames) {
            e.rule.message = "Unexpected File Name."; // Toggles to file name message
            return hasValidNames;
        };

        if(hasValidCount && hasValidNames){
            e.rule.message = ''; // Toggles to empty message
        }

        return isValid;
    }

    return (
        <Form className={classNames({ 'plain-styled-form': true, 'view-mode': !editing })}
            labelMode='floating'>
            <GroupItem caption='Upload Details'>
                <GroupItem colCount={2}>
                    <FormItem>
                        <DateBox
                            value={formDataDefaults?.BillDate}
                            calenderOptions={billDateOptions}
                            readOnly={!editing}
                            label="Bill Date"
                            inputAttr={{ class: 'form-editor-input' }}
                            stylingMode='filled'
                            displayFormat={"MM/yyyy"}
                            dateSerializationFormat='MM-dd-yyyy'
                            placeholder='MM/yyyy'
                            useMaskBehavior={true}
                            onValueChange={e => onDataChanged('BillDate', e)}>
                            <Validator>
                                <RequiredRule message={'Bill Date is required'} />
                            </Validator>
                        </DateBox>
                    </FormItem>
                    <FormItem>
                        <DateBox
                            value={formDataDefaults?.BillPeriod}
                            displayFormat="monthAndYear"
                            pickerType="calendar"
                            calendarOptions={{
                                zoomLevel: "year",          // Starts the calendar in month-selection view
                                minZoomLevel: "decade",    // Prevents zooming out to centuries
                                maxZoomLevel: "year"       // Prevents zooming in to days
                            }}
                            isEditing={editing}
                            label="Bill Period"
                            inputAttr={{ class: 'form-editor-input' }}
                            stylingMode='filled'
                            placeholder='MMMM yyyy'
                            useMaskBehavior={true}
                            onValueChange={e => onDataChanged('BillPeriod', e)}                            type='date'>
                            <Validator>
                                <RequiredRule message={'Bill Period is required'} />
                            </Validator>
                        </DateBox>
                    </FormItem>
                </GroupItem>
                <FormItem cssClass='accent' label='File Upload'>
                    <div className="fileuploader-container">
                        <span className="note">
                            Allowed file extensions: <span>{fileExtensions.join(" and ")}</span>.
                        </span>
                        <span className="note">
                            Maximum file size: <span>25 MB.</span>
                        </span>
                        <FileUploader
                            multiple={true}
                            uploadMode="useForm"
                            accept={fileExtensions.join(" ,")}
                            allowedFileExtensions={fileExtensions}
                            maxFileSize={25000000}
                            readOnly={!editing}
                            selectButtonText="Select File"
                            labelText="or Drop File Here"
                            onValueChanged={e => onDataChanged("UploadFiles", e.value)}>
                            <Validator>
                                <RequiredRule message={'File is required.'} />
                                <CustomRule message='File Error.' reevaluate={true}
                                 validationCallback={(e) => hasValidFiles(e)}/>
                            </Validator>
                        </FileUploader>
                    </div>
                </FormItem>
            </GroupItem>
        </Form>
    );
};